import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useNavigate } from '@tanstack/react-router';
import { FilterableSelect } from '../components/common/FilterableSelect';
import {
  DESCRIPTOR_CATEGORIES_BY_TYPE,
  ENTITY_TYPES,
  useCreateEntity,
  useEntityArchetypes,
  useBatchAddDescriptors,
  type EntityTypeSlug,
} from '../api/queries/entities';
import { createApiClient } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { EntityDescriptorsEditor } from '../components/entities/EntityDescriptorsEditor';
import { EntityImageGallery } from '../components/entities/EntityImageGallery';
import { VoiceProfileTab } from '../components/entities/VoiceProfileTab';
import { GenerationModal } from '../components/ai/GenerationModal';

const STEPS = ['Basics', 'AI Profile', 'Descriptors', 'Images', 'Voice'];

export function EntityCreateWizardRoute() {
  const navigate = useNavigate();
  const createEntity = useCreateEntity();
  const { data: archetypes = [] } = useEntityArchetypes();
  const { getToken } = useAuth();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [entityId, setEntityId] = useState<string | null>(null);

  // Step 1 state
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState<EntityTypeSlug>('character');
  const [description, setDescription] = useState('');
  const [selectedArchetypeId, setSelectedArchetypeId] = useState<string | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Step 2 state (AI generation)
  const [generating, setGenerating] = useState(false);
  const [generatedDescriptors, setGeneratedDescriptors] = useState<
    { category: string; content: string; selected: boolean }[]
  >([]);
  const [genError, setGenError] = useState<string | null>(null);

  // Step 4 state (images)
  const [genModalOpen, setGenModalOpen] = useState(false);

  // Batch add hook (only available after entity created)
  const batchAdd = useBatchAddDescriptors(entityId ?? '');

  const isCharacter = entityType === 'character';
  const visibleSteps = isCharacter ? STEPS : STEPS.filter((s) => s !== 'Voice');

  // Filter archetypes by selected type
  const filteredArchetypes = useMemo(
    () => archetypes.filter((a) => a.entityType === entityType),
    [archetypes, entityType],
  );

  const selectedArchetype = archetypes.find(
    (a) => a.id === selectedArchetypeId,
  );

  // --- Step 1: Create entity ---
  const handleCreateEntity = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createEntity.mutateAsync({
        name: name.trim(),
        entityType,
        description: description.trim() || undefined,
        skipDuplicateCheck: true,
      });
      if (result.kind === 'created') {
        const newId = result.entity.id;
        setEntityId(newId);

        // If archetype selected, pre-populate descriptors
        if (selectedArchetype?.descriptors) {
          try {
            const descs = selectedArchetype.descriptors as {
              category: string;
              content: string;
            }[];
            if (Array.isArray(descs) && descs.length > 0) {
              const client = createApiClient(getToken);
              await client.post(`api/v2/entities/${newId}/descriptors/batch`, {
                json: { descriptors: descs },
              });
            }
          } catch {
            // Non-critical — user can add descriptors manually
          }
        }

        setActiveStep(1);
      } else if (result.kind === 'duplicates') {
        setCreateError(
          `Found ${result.suggestions.length} similar entit${result.suggestions.length === 1 ? 'y' : 'ies'}. Change the name or re-submit.`,
        );
      }
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create entity',
      );
    } finally {
      setCreating(false);
    }
  };

  // --- Step 2: AI Profile Generation ---
  const handleGenerateProfile = async () => {
    if (!entityId || !description.trim()) return;
    setGenerating(true);
    setGenError(null);
    try {
      const client = createApiClient(getToken);

      // Call the text-generation action with a structured prompt for descriptors
      const categories = DESCRIPTOR_CATEGORIES_BY_TYPE[entityType] ?? [];
      const prompt = [
        `Generate detailed descriptors for a ${entityType} named "${name}".`,
        `Description: ${description}`,
        '',
        `Generate one descriptor for each of these categories: ${categories.join(', ')}.`,
        `Each descriptor should be 2-4 sentences of rich, specific detail.`,
        '',
        `Return ONLY a JSON array of objects, each with "category" and "content" fields.`,
        `Example: [{"category": "appearance", "content": "Tall and lean with..."}]`,
      ].join('\n');

      const resp = await client
        .post('api/v2/ai-actions/execute/text-generation', {
          json: {
            prompt,
            max_words: 2000,
          },
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        })
        .json<{ data: { jobId: string } }>();

      // Poll for result
      const jobId = resp.data.jobId;
      let attempts = 0;
      while (attempts < 30) {
        await new Promise((r) => setTimeout(r, 2000));
        const status = await client
          .get(`api/v2/jobs/${jobId}`)
          .json<{ data: { status: string; result?: string } }>();

        if (status.data.status === 'completed' && status.data.result) {
          // Try to parse JSON from the result
          const text = status.data.result;
          const jsonMatch = text.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]) as {
              category: string;
              content: string;
            }[];
            setGeneratedDescriptors(
              parsed.map((d) => ({ ...d, selected: true })),
            );
          }
          break;
        }
        if (status.data.status === 'failed') {
          setGenError(
            'AI generation failed. You can add descriptors manually.',
          );
          break;
        }
        attempts++;
      }
      if (attempts >= 30) {
        setGenError('Generation timed out. You can add descriptors manually.');
      }
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleAcceptDescriptors = async () => {
    if (!entityId) return;
    const selected = generatedDescriptors.filter((d) => d.selected);
    if (selected.length === 0) {
      setActiveStep(2);
      return;
    }
    await batchAdd.mutateAsync(
      selected.map((d) => ({ category: d.category, content: d.content })),
    );
    setActiveStep(2);
  };

  const toggleDescriptor = (index: number) => {
    setGeneratedDescriptors((prev) =>
      prev.map((d, i) => (i === index ? { ...d, selected: !d.selected } : d)),
    );
  };

  // --- Navigation ---
  const handleNext = () => {
    const maxStep = visibleSteps.length - 1;
    if (activeStep < maxStep) setActiveStep(activeStep + 1);
  };

  const handleFinish = () => {
    if (entityId) {
      navigate({ to: '/entities/$entityId', params: { entityId } });
    }
  };

  // Build descriptor seed for image generation
  const descriptorSeed = useMemo(() => {
    if (generatedDescriptors.length > 0) {
      return generatedDescriptors
        .filter((d) => d.selected)
        .map((d) => `${d.category}: ${d.content}`)
        .join('. ');
    }
    return `${entityType} named ${name}. ${description}`;
  }, [generatedDescriptors, entityType, name, description]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Create Entity
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {visibleSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Basics */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h6">Basic Information</Typography>

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              autoFocus
            />

            <FilterableSelect
              label="Type"
              value={entityType}
              onChange={(v) => {
                setEntityType((v || 'character') as EntityTypeSlug);
                setSelectedArchetypeId(null);
              }}
              options={ENTITY_TYPES}
              allowEmpty={false}
              fullWidth
            />

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Brief description — this will be used to generate AI-assisted descriptors in the next step"
            />

            {/* Archetype selection */}
            {filteredArchetypes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Start from archetype (optional)
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  {filteredArchetypes.map((a) => (
                    <Chip
                      key={a.id}
                      label={a.name}
                      variant={
                        selectedArchetypeId === a.id ? 'filled' : 'outlined'
                      }
                      color={
                        selectedArchetypeId === a.id ? 'primary' : 'default'
                      }
                      onClick={() =>
                        setSelectedArchetypeId(
                          selectedArchetypeId === a.id ? null : a.id,
                        )
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {createError && <Alert severity="error">{createError}</Alert>}

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!name.trim() || creating}
                onClick={handleCreateEntity}
              >
                {creating ? 'Creating...' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Step 2: AI Profile */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h6">AI-Assisted Profile</Typography>
            <Typography variant="body2" color="text.secondary">
              Generate descriptors automatically from your description, or skip
              to add them manually.
            </Typography>

            {generatedDescriptors.length === 0 && !generating && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleGenerateProfile}
                  disabled={!description.trim()}
                >
                  Generate Profile
                </Button>
                <Button variant="outlined" onClick={() => setActiveStep(2)}>
                  Skip
                </Button>
              </Stack>
            )}

            {generating && (
              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Generating profile for {name}...
                </Typography>
              </Stack>
            )}

            {genError && <Alert severity="warning">{genError}</Alert>}

            {generatedDescriptors.length > 0 && (
              <>
                <Typography variant="subtitle2">
                  Generated descriptors — select which to keep:
                </Typography>
                <Stack spacing={1}>
                  {generatedDescriptors.map((d, i) => (
                    <Card key={i} variant="outlined">
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={d.selected}
                              onChange={() => toggleDescriptor(i)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="caption"
                                color="primary"
                                fontWeight={600}
                              >
                                {d.category}
                              </Typography>
                              <Typography variant="body2">
                                {d.content}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => setActiveStep(2)}>
                    Skip
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAcceptDescriptors}
                    disabled={batchAdd.isPending}
                  >
                    {batchAdd.isPending
                      ? 'Adding...'
                      : `Add ${generatedDescriptors.filter((d) => d.selected).length} Descriptors`}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* Step 3: Descriptors */}
      {activeStep === 2 && entityId && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Descriptors</Typography>
            <Typography variant="body2" color="text.secondary">
              Add, edit, or remove descriptors. These define your entity's
              profile.
            </Typography>
            <EntityDescriptorsEditor
              entityId={entityId}
              entityType={entityType}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Step 4: Images */}
      {activeStep === 3 && entityId && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Reference Images</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload reference images or generate with AI.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={() => setGenModalOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Generate with AI
            </Button>
            <EntityImageGallery entityId={entityId} />
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={handleNext}>
                {isCharacter ? 'Next' : 'Finish'}
              </Button>
            </Stack>
          </Stack>

          <GenerationModal
            open={genModalOpen}
            onClose={() => setGenModalOpen(false)}
            mode="image"
            seedPrompt={descriptorSeed}
          />
        </Paper>
      )}

      {/* Step 5: Voice (characters only) */}
      {activeStep === 4 && entityId && isCharacter && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Voice Profile</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the voice for this character. This can also be done
              later.
            </Typography>
            <VoiceProfileTab entityId={entityId} />
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={handleFinish}>
                Finish
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Non-character finish at step 4 */}
      {activeStep === 3 && !isCharacter && entityId && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleFinish}>
            Finish
          </Button>
        </Stack>
      )}
    </Box>
  );
}
